import { cn } from '@/lib/utils';
import { useIsSafeMode } from '@/lib/safeMode';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SafeSelectProps {
  value?: string;
  onValueChange?: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  /** Force native select regardless of safe mode */
  forceNative?: boolean;
  /** Aria label for accessibility */
  'aria-label'?: string;
  /** Name attribute for forms */
  name?: string;
}

/**
 * Select component that automatically switches to native <select>
 * when Safe Mode is enabled or when forceNative is true.
 *
 * Use this for all select inputs in forms to ensure users can
 * always complete their work even if Radix portals fail.
 */
export function SafeSelect({
  value,
  onValueChange,
  options,
  placeholder,
  disabled,
  className,
  forceNative,
  'aria-label': ariaLabel,
  name,
}: SafeSelectProps) {
  const isSafeMode = useIsSafeMode();

  if (forceNative || isSafeMode) {
    return (
      <NativeSelect
        value={value}
        onValueChange={onValueChange}
        options={options}
        placeholder={placeholder}
        disabled={disabled}
        className={className}
        aria-label={ariaLabel}
        name={name}
      />
    );
  }

  return (
    <Select value={value} onValueChange={onValueChange} disabled={disabled} name={name}>
      <SelectTrigger className={className} aria-label={ariaLabel}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value} disabled={option.disabled}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/**
 * Native select - always works, styled to match design system
 */
function NativeSelect({
  value,
  onValueChange,
  options,
  placeholder,
  disabled,
  className,
  'aria-label': ariaLabel,
  name,
}: SafeSelectProps) {
  return (
    <select
      value={value ?? ''}
      onChange={(e) => onValueChange?.(e.target.value)}
      disabled={disabled}
      aria-label={ariaLabel}
      name={name}
      className={cn(
        // Base styles matching shadcn Select
        'flex h-9 w-full items-center justify-between rounded-md',
        'border border-input bg-transparent px-3 py-2',
        'text-sm shadow-sm',
        'ring-offset-background',
        'focus:outline-none focus:ring-1 focus:ring-ring',
        'disabled:cursor-not-allowed disabled:opacity-50',
        // Native select specific - custom dropdown arrow
        'appearance-none cursor-pointer',
        "bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%236b7280%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpath%20d%3D%22m6%209%206%206%206-6%22%2F%3E%3C%2Fsvg%3E')]",
        'bg-[length:16px] bg-[right_8px_center] bg-no-repeat',
        'pr-8',
        // Placeholder styling when no value selected
        !value && 'text-muted-foreground',
        className
      )}
    >
      {placeholder && (
        <option value="" disabled hidden>
          {placeholder}
        </option>
      )}
      {options.map((option) => (
        <option key={option.value} value={option.value} disabled={option.disabled}>
          {option.label}
        </option>
      ))}
    </select>
  );
}

/**
 * Export NativeSelect for direct use when you always want native
 */
export { NativeSelect };
